// Generated by tutorials/record.cjs from tutorials/articles/h21-change-diet-order.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h21 --text-only` (wording) or
// `node tutorials/record.cjs h21` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'change-diet-order',
  catalogId: 'h21',
  title: 'Changing a diet order',
  summary: 'Change a resident\'s diet types and consistencies, check what the new diet changes on their meal pattern, and see the new order on their tray ticket.',
  audience: 'Dietitians and dietary managers who enter diet orders',
  area: 'residents',
  learn: [
    'Change a diet type or a consistency on the Diet Order section',
    'Review the meal pattern changes the new diet brings',
    'Check the new order on the tray ticket',
    'See every change in Diet Fluctuations, and enter one that starts later',
  ],
  keywords: [
    'diet order',
    'change diet',
    'diet change',
    'consistency',
    'texture',
    'mechanical soft',
    'pureed',
    'thickened liquids',
    'diet fluctuations',
    'auto item modifications',
    'tray ticket',
  ],
  steps: [
    {
      id: 'open-diet-order',
      part: 'Change the order',
      heading: 'Open the Diet Order section',
      text: 'Open the resident from the Residents page. On the left, select Diet Order. You see up to five diet types and five consistencies.',
      media: {
        image: {
          src: '/help/change-diet-order/open-diet-order.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/change-diet-order/open-diet-order.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.5,
        },
        alt: 'The Diet Order section for Pembroke, Arthur: Diet Type 1 Regular, Consistency 1 Regular and Consistency 2 Thin Liquids.',
      },
    },
    {
      id: 'change-consistency',
      part: 'Change the order',
      heading: 'Pick the new diet or consistency',
      text: 'Open the box you want to change and pick the new one. Here the speech therapist ordered a mechanical soft texture, so Consistency 1 changes from Regular to Mechanical Soft.\n\nTo take one off the order, select the X in its box.',
      media: {
        image: {
          src: '/help/change-diet-order/change-consistency.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/change-diet-order/change-consistency.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.8,
        },
        alt: 'Consistency 1 changed to Mechanical Soft, and Save Changes (1) at the top right.',
      },
    },
    {
      id: 'save',
      part: 'Change the order',
      heading: 'Save the change',
      text: 'Select Save Changes at the top right. Dietech saves the new order and checks the resident\'s meal pattern and adjustments against it.',
      media: {
        image: {
          src: '/help/change-diet-order/save.webp',
          width: 1280,
          height: 646,
        },
        clip: {
          src: '/help/change-diet-order/save.mp4',
          width: 1280,
          height: 646,
          durationSec: 2,
        },
        alt: 'The Auto Item Modifications window, listing two meal pattern items and how the new diet changes them.',
      },
    },
    {
      id: 'review-changes',
      part: 'Change the order',
      heading: 'Review the meal pattern changes',
      text: 'When your facility has auto item modifications set up, Dietech shows what the new diet changes on the meal pattern. Old is the item now, and New is what it becomes, with the change marked.\n\nClear Accept for any change you do not want, then select Apply.',
      media: {
        image: {
          src: '/help/change-diet-order/review-changes.webp',
          width: 1280,
          height: 646,
        },
        alt: 'Whole Wheat Toast changes from Regular to Mechanical Soft at breakfast, and Fresh Apple becomes Applesauce at dinner, both with Accept checked.',
      },
    },
    {
      id: 'applied',
      part: 'Change the order',
      heading: 'Back on the Residents page',
      text: 'Select Apply. You go back to the Residents page, which shows the new diet order. A message says whether the resident\'s selective menus needed updating.',
      media: {
        image: {
          src: '/help/change-diet-order/applied.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/change-diet-order/applied.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Residents page with Pembroke, Arthur showing Regular, Mechanical Soft, Thin Liquids, and a message that the diet change was applied.',
      },
    },
    {
      id: 'ticket',
      part: 'See it on the tray ticket',
      heading: 'Check the tray ticket',
      text: 'With the resident selected, select Visualizer. The ticket shows the new diet order at the top, and each food item now prints in the new consistency.',
      media: {
        image: {
          src: '/help/change-diet-order/ticket.webp',
          width: 1280,
          height: 794,
        },
        clip: {
          src: '/help/change-diet-order/ticket.mp4',
          width: 1280,
          height: 794,
          durationSec: 2,
        },
        alt: 'The Tray Ticket Visualizer for Pembroke, Arthur: Regular, Mechanical Soft and Thin Liquids at the top, and the breakfast items marked Mechanical Soft.',
      },
    },
    {
      id: 'history',
      part: 'See it on the tray ticket',
      heading: 'See the change in Diet Fluctuations',
      text: 'Open the resident again and select Diet Fluctuations. Every diet order is listed with the date it started, newest first.\n\nTo enter an order that starts later, or one that ends on a date, such as clear liquids before a procedure, select New here.',
      media: {
        image: {
          src: '/help/change-diet-order/history.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/change-diet-order/history.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.8,
        },
        alt: 'Diet Fluctuations listing the new order, Regular, Mechanical Soft, Thin Liquids, dated today, above the earlier order.',
      },
    },
  ],
  tips: [
    'Put the food texture in Consistency 1 and the liquid consistency in Consistency 2, the same way for every resident, so tickets and reports read the same.',
    'A diet type or consistency with no check under In MC has no menus written for it in your menu cycle. Tell whoever plans your menus.',
  ],
  notes: [
    'The new order counts from the moment you save. Tray tickets that already printed keep the old order, so reprint the resident\'s ticket if the meal has not gone out yet.',
    'If the Diet Order section says the current diet has an end date or there are future diets, change the order in Diet Fluctuations instead.',
    'Auto item modifications only appear when your facility has set them up for that diet or consistency.',
  ],
  related: ['admit-resident', 'meal-patterns', 'tray-tickets'],
  recorded: '2026-10-25',
};

export default article;
